import { createContext, useContext } from 'react';
import type { UserData } from '../../services/auth';

export interface AuthContextValue {
  user: UserData | null;
  loading: boolean;
  isAuthenticated: () => boolean;

  login: (email: string, password: string) => Promise<{
    success: boolean;
    user?: UserData;
    error?: string;
    errorCode?: string;
  }>;
  logout: () => Promise<void>;
  register: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<{
    success: boolean;
    error?: string;
    errorCode?: string;
  }>;

  resendVerificationEmail: (email: string) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
    errorCode?: string;
    retryAfterSeconds?: number;
  }>;

  requestPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyResetToken: (email: string, token: string) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  resetPassword: (password: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
    needsReauth?: boolean;
    recommendReauth?: boolean;
  }>;
  updateEmail: (newEmail: string, currentPassword: string) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }>;

  processOAuthTokens: (hash: string) => Promise<boolean>;
  refreshUserData: (options?: { maxRetries?: number; retryDelay?: number; forceTokenRefresh?: boolean }) => Promise<boolean>;
  updateUserData: (data: Partial<UserData>) => void;

  /** @deprecated Use NFTMarketplaceContext instead */
  connectedWalletAddress: string | null;
  /** @deprecated Use NFTMarketplaceContext instead */
  isWalletConnected: boolean;
  /** @deprecated Use NFTMarketplaceContext instead */
  isWalletConnecting: boolean;
  /** @deprecated Use NFTMarketplaceContext instead */
  connectWallet: () => Promise<{ success: boolean; error?: string }>;
  /** @deprecated Use NFTMarketplaceContext instead */
  disconnectWallet: () => Promise<{ success: boolean; error?: string }>;
  /** @deprecated Use NFTMarketplaceContext instead */
  connectWalletError: Error | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export type { UserData };
